import type { SettingChange } from '@/components/settings/settings-save.types';

/** Subset of RHF `dirtyFields` for equipment details (avoids version-specific RHF type exports). */
export type DetailsFormDirtyFields = {
  settings?: Record<string, unknown>;
  sequencerSettings?: Record<string, unknown>;
  sequencer?: Record<string, unknown>;
  defrostSchedule?: unknown;
};

/**
 * Counts leaf `true` markers in a React Hook Form `dirtyFields` subtree.
 * Used when `SettingChange[]` omits edits (e.g. unmatched scalar IDs or defrost without `settingId`)
 * but the form is still dirty.
 */
export function countRhfDirtyLeaves(node: unknown): number {
  if (node === true) return 1;
  if (node === false || node === undefined || node === null) return 0;
  if (Array.isArray(node)) {
    return node.reduce<number>((sum, item) => sum + countRhfDirtyLeaves(item), 0);
  }
  if (typeof node === 'object') {
    return Object.values(node as Record<string, unknown>).reduce<number>(
      (sum, value) => sum + countRhfDirtyLeaves(value),
      0
    );
  }
  return 0;
}

const getSectionKey = (change: SettingChange): string | undefined =>
  (change.metadata as { sectionKey?: string } | undefined)?.sectionKey;

const countChangesInSection = (changes: SettingChange[], sectionKey: string): number =>
  changes.filter((c) => getSectionKey(c) === sectionKey).length;

const hasDefrostScheduleDirty = (defrostDirty: unknown): boolean =>
  !!(defrostDirty && (Array.isArray(defrostDirty) ? defrostDirty.length > 0 : defrostDirty));

/**
 * Save-bar change count: includes every `SettingChange` plus dirty form fields that did not
 * map into a change (so the bar never shows "0 … changed" while `formState.isDirty` is true).
 */
export function getEquipmentDetailsSaveBarChangeCount(
  dirtyFields: DetailsFormDirtyFields | undefined,
  settingChanges: SettingChange[]
): number {
  if (!dirtyFields) {
    return settingChanges.length;
  }

  const dirtySettingsKeys = Object.keys((dirtyFields.settings as Record<string, unknown>) ?? {});
  const dirtySequencerSettingsKeys = Object.keys(
    (dirtyFields.sequencerSettings as Record<string, unknown>) ?? {}
  );
  const dirtySequencerKeys = Object.keys((dirtyFields.sequencer as Record<string, unknown>) ?? {});

  const settingsChangeCount = countChangesInSection(settingChanges, 'settings');
  const sequencerSettingsChangeCount = countChangesInSection(settingChanges, 'sequencerSettings');
  const sequencerChangeCount = countChangesInSection(settingChanges, 'sequencer');
  const defrostChangeCount = countChangesInSection(settingChanges, 'defrostSchedule');

  let orphanBonus = 0;
  orphanBonus += Math.max(0, dirtySettingsKeys.length - settingsChangeCount);
  orphanBonus += Math.max(0, dirtySequencerSettingsKeys.length - sequencerSettingsChangeCount);
  orphanBonus += Math.max(0, dirtySequencerKeys.length - sequencerChangeCount);

  const defrostDirty = dirtyFields.defrostSchedule;
  if (hasDefrostScheduleDirty(defrostDirty) && defrostChangeCount === 0) {
    orphanBonus += Math.max(1, countRhfDirtyLeaves(defrostDirty));
  }

  return settingChanges.length + orphanBonus;
}
